/**
 * connectWarmupState — the connect-intent warmup's shared state store
 * (2026-08-31 instant-connect). Zero dependencies BY DESIGN: the click path
 * (desktopSignIn.ts) reads this store, and the warmup runner
 * (connectIntentWarmup.ts) writes it — splitting the store out keeps the
 * click path free of the runner's boot-gate/resolver import graph and kills
 * the module cycle between the two.
 */

/** How long a warmup's gate answer stands in for the click-time gate hop.
 *  Short on purpose: the idle ladder moves in hours, but a stale 'ready'
 *  costs a confusing failure, while a re-check costs ~a second. */
export const WARMUP_GATE_TTL_MS = 60_000;
/** How long the health-verified VM base stands in for the click-time
 *  Firestore read + probe. */
export const WARMUP_VM_TTL_MS = 60_000;
/** Re-running the full warmup for the same target more often than this is
 *  pure waste (card re-renders, tab focus churn). */
export const WARMUP_RERUN_DEBOUNCE_MS = 30_000;

export interface WarmupVmInfo {
  base: string;
  accountId: string;
  token: string | null;
  verifiedAt: number;
}

export interface ConnectWarmupState {
  /** When the gate last answered ready/unknown for this target (null = it
   *  didn't — asleep, provisioning, none, or the warmup hasn't finished). */
  gateReadyAt: number | null;
  /** The account the status endpoint answered for (its echo — the
   *  pending-org substitution included). */
  gateAccountId: string | null;
  /** Health-verified account-VM browser base, when one was reachable. */
  vm: WarmupVmInfo | null;
}

export interface WarmupEntry extends ConnectWarmupState {
  startedAt: number;
}

const stateByTarget = new Map<string, WarmupEntry>();

export const warmupKeyFor = (workspaceAccountId?: string): string =>
  workspaceAccountId || 'ambient';

/** The runner's write side: the prior entry for debounce checks, or null. */
export function getWarmupEntry(workspaceAccountId?: string): WarmupEntry | null {
  return stateByTarget.get(warmupKeyFor(workspaceAccountId)) ?? null;
}

/** The runner's write side: start a fresh entry for this target. */
export function createWarmupEntry(workspaceAccountId: string | undefined, startedAt: number): WarmupEntry {
  const entry: WarmupEntry = { startedAt, gateReadyAt: null, gateAccountId: null, vm: null };
  stateByTarget.set(warmupKeyFor(workspaceAccountId), entry);
  return entry;
}

/**
 * The click path's read side (consumed by startSignInDesktopSession): the
 * freshest warmup state for this target, or null when none ran.
 */
export function getConnectWarmupState(workspaceAccountId?: string): ConnectWarmupState | null {
  const entry = getWarmupEntry(workspaceAccountId);
  if (!entry) return null;
  return { gateReadyAt: entry.gateReadyAt, gateAccountId: entry.gateAccountId, vm: entry.vm };
}

/** @internal — for unit tests only. */
export function __resetConnectWarmupStateForTests(): void {
  stateByTarget.clear();
}
